import React from "react";
import { VStack, HStack } from "../Stack/Stack";
import { Text } from "../Text/Text";
import { InfoTooltip } from "../InfoTooltip/InfoTooltip";

interface DetailLineProps {
  /** Section label. Always rendered uppercase, regardless of casing passed in. */
  title: string;
  /** The value to display. Usually text, but can be any JSX (e.g. a photo grid). */
  detail: React.ReactNode;
  /** Icon rendered before the detail content. */
  icon?: React.ReactElement;
  /** Adds a bottom border under the detail row, styled like an underlined field. */
  underlined?: boolean;
  /** Adds an info icon next to the title; hover or tap reveals this text. */
  tooltip?: string;
  /** Title color override. Defaults to the "bold" color, gray.900. */
  titleColor?: string;
  /** Text-detail color override. Defaults to the "body copy" color, gray.700.
   * Only applies when `detail` is plain text — JSX detail manages its own color. */
  detailColor?: string;
}

export const DetailLine: React.FC<DetailLineProps> = ({
  title,
  detail,
  icon,
  underlined,
  tooltip,
  titleColor = "gray.900",
  detailColor = "gray.700",
}) => {
  const isTextDetail = typeof detail === "string" || typeof detail === "number";

  return (
    <VStack align="start" spacing={icon ? "8px" : "4px"} w="full">
      <HStack spacing="4px">
        <Text
          as="span"
          m="0"
          fontSize="xxs"
          fontWeight="bold"
          color={titleColor}
          textTransform="uppercase"
          letterSpacing="wide"
        >
          {title}
        </Text>
        {tooltip && <InfoTooltip text={tooltip} size="14px" />}
      </HStack>
      <HStack
        spacing="8px"
        w="full"
        pb={underlined ? "8px" : undefined}
        borderBottom={underlined ? "1px solid" : undefined}
        borderColor={underlined ? "gray.300" : undefined}
      >
        {icon}
        {isTextDetail ? (
          <Text m="0" w="full" fontSize="md" color={detailColor} whiteSpace="pre-line">
            {detail}
          </Text>
        ) : (
          detail
        )}
      </HStack>
    </VStack >
  );
};
